import { Trans, useLingui } from "@lingui/react/macro";
import { Text } from "@medoma/components/Text/Text";
import styles from "./List.module.scss";
import type { ShiftsWithContents } from "@/api/Shifts";
import { ShiftWithRoutesAndActivities } from "./ShiftWithRoutesAndActivities";
import {
  useFilteredShiftsWithContents,
  type ShiftsBySection,
} from "../useFilteredShiftsWithContents";
import { usePatientOrActivityTitleFilter } from "../PlanningContext";
import { useRevealNonMatchingShifts } from "../useRevealNonMatchingShifts";
import { type ShiftWithFilteredContents } from "../shiftWithFilteredContents";
import { ShowNonMatchingShiftsButton } from "../ShowNonMatchingShiftsButton";
import NoResults from "@/components/NoResults/NoResults";
import { usePlanningViewToolbar } from "../usePlanningViewToolbar";

const orderedShifts = (
  shiftsBySection: ShiftsBySection,
): ShiftWithFilteredContents[] => [
  ...shiftsBySection.currentUsersShifts,
  ...shiftsBySection.staffedShifts,
  ...shiftsBySection.notStaffedShifts,
];

const ShiftList = ({
  shiftsWithFilteredContents,
}: {
  shiftsWithFilteredContents: ShiftWithFilteredContents[];
}) => {
  const { isExpanded } = usePlanningViewToolbar();

  return (
    <>
      {shiftsWithFilteredContents.map((shiftWithFilteredContents) => (
        <ShiftWithRoutesAndActivities
          key={shiftWithFilteredContents.shiftWithContents.shift.id}
          shiftWithContents={shiftWithFilteredContents.shiftWithContents}
          matchedItemIds={shiftWithFilteredContents.matchedItemIds}
          isExpanded={isExpanded}
        />
      ))}
    </>
  );
};

export const List = ({
  shiftsWithContents,
}: {
  shiftsWithContents: ShiftsWithContents;
}) => {
  const { t } = useLingui();
  const { value: filterValue } = usePatientOrActivityTitleFilter();
  const { areNonMatchingShiftsRevealed, revealNonMatchingShifts } =
    useRevealNonMatchingShifts(filterValue);
  const {
    matching,
    nonMatching,
    isFilterActive,
    hasNonMatchingShifts,
    numberOfNonMatchingShifts,
    noShiftsMatchSearch: noRelevantShiftsMatchSearch,
  } = useFilteredShiftsWithContents(shiftsWithContents.relevant);
  const numberOfShiftsWithContentsBeforeSelectedTimeFilter =
    shiftsWithContents.before.length;
  const numberOfShiftsWithContentsAfterSelectedTimeFilter =
    shiftsWithContents.after.length;

  return (
    <section className={styles.listSection}>
      {numberOfShiftsWithContentsBeforeSelectedTimeFilter >= 1 ? (
        <div className={styles.hiddenShiftsMessage}>
          <Text element="span" size="small">
            <Trans>
              {numberOfShiftsWithContentsBeforeSelectedTimeFilter} tidigare
              arbetspass har dolts av tidsinställningarna
            </Trans>
          </Text>
        </div>
      ) : null}

      {/* Shifts withing selected timespan */}
      {noRelevantShiftsMatchSearch ? (
        <NoResults
          message={t`Inga arbetspass matchar sökningen`}
          padding={12}
        />
      ) : (
        <ShiftList shiftsWithFilteredContents={orderedShifts(matching)} />
      )}

      {/* Shifts not matching current search filter - hidden by default, visible on action */}
      {isFilterActive && hasNonMatchingShifts ? (
        areNonMatchingShiftsRevealed ? (
          <ShiftList shiftsWithFilteredContents={orderedShifts(nonMatching)} />
        ) : (
          <div className={styles.revealNonMatchingShifts}>
            <ShowNonMatchingShiftsButton
              numberOfNonMatchingShifts={numberOfNonMatchingShifts}
              onClick={revealNonMatchingShifts}
            />
          </div>
        )
      ) : null}

      {numberOfShiftsWithContentsAfterSelectedTimeFilter >= 1 ? (
        <div className={styles.hiddenShiftsMessage}>
          <Text element="span" size="small">
            <Trans>
              {numberOfShiftsWithContentsAfterSelectedTimeFilter} senare
              arbetspass har dolts av tidsinställningarna
            </Trans>
          </Text>
        </div>
      ) : null}
    </section>
  );
};
